import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { AddOn, Category, MenuResponse, Product, ProductVariant, ProductOptionGroup, ChosenOption} from '../api/types';
import { Alert, Spinner, assetUrl, money } from '../components/common';
import { useCart } from '../state/CartContext';

export function MenuPage() {
  const [menu, setMenu] = useState<MenuResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ product: Product; earnsStamp: boolean } | null>(null);

  useEffect(() => {
    api
      .menu()
      .then((response) => {
        setMenu(response);
        setActiveGroup(response.categories[0]?.group ?? null);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  const groups = useMemo(() => {
    if (!menu) return [];

    // Preserve the order the API returns; it mirrors the printed menu.
    return menu.categories.reduce<string[]>(
      (all, category) => (all.includes(category.group) ? all : [...all, category.group]),
      [],
    );
  }, [menu]);

  const visible = useMemo(
    () => (menu ? menu.categories.filter((c) => c.group === activeGroup) : []),
    [menu, activeGroup],
  );

  if (error) {
    return <Alert>We couldn&apos;t load the menu: {error}</Alert>;
  }

  if (!menu) {
    return <Spinner label="Loading the menu…" />;
  }

  return (
    <>
      <section className="hero">
        <h1>
          Order fresh. <span>Delivered.</span>
        </h1>
        <p>
          Smoothies, juices, protein shakes and breakfast bowls, blended to order and delivered
          across <strong>Randburg</strong> and <strong>Sandton</strong>. Same prices as in store —
          we don&apos;t mark the menu up for the delivery apps.
        </p>
      </section>

      <nav className="grouptabs" aria-label="Menu sections">
        {groups.map((group) => (
          <button
            key={group}
            type="button"
            className={group === activeGroup ? 'grouptab is-active' : 'grouptab'}
            onClick={() => setActiveGroup(group)}
            aria-pressed={group === activeGroup}
          >
            {group}
          </button>
        ))}
      </nav>

      {visible.map((category) => (
        <CategorySection
          key={category.id}
          category={category}
          onPick={(product) => setSelected({ product, earnsStamp: category.earnsLoyaltyStamp })}
        />
      ))}

      {selected && (
        <ProductDialog
          product={selected.product}
          earnsStamp={selected.earnsStamp}
          addOns={menu.addOns}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

function CategorySection({ category, onPick }: { category: Category; onPick: (p: Product) => void }) {
  return (
    <section className="category">
      <h2 className="category__title">
        {category.name}
        {category.earnsLoyaltyStamp && (
          <span className="tag tag--stamp" title="Earns a loyalty stamp">
            🥤 earns a stamp
          </span>
        )}
      </h2>

      <div className="cards">
        {category.products.map((product) => (
          <article key={product.id} className="card">
            {product.imageUrl && (
              <img
                className="card__img"
                src={assetUrl(product.imageUrl) ?? undefined}
                alt={product.name}
                loading="lazy"
                width={400}
                height={400}
              />
            )}
            <div className="card__body">
              <h3>{product.name}</h3>
              {product.description && <p className="card__desc">{product.description}</p>}
              <div className="card__foot">
                <span className="card__price">
                  {product.variants.length > 1 ? 'from ' : ''}
                  {money(product.startingPrice ?? product.variants[0]?.price ?? 0)}
                </span>
                <button type="button" className="btn btn--primary btn--sm" onClick={() => onPick(product)}>
                  Add
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function ProductDialog({
  product,
  earnsStamp,
  addOns,
  onClose,
}: {
  product: Product;
  earnsStamp: boolean;
  addOns: AddOn[];
  onClose: () => void;
}) {
  const { add } = useCart();
  const [variant, setVariant] = useState<ProductVariant>(product.variants[0]);
  const [chosenAddOns, setChosenAddOns] = useState<AddOn[]>([]);
  // Picks per option group, as a list of choice ids so the same flavour can be
  // taken more than once — four Berry Blasts is a legitimate box.
  const [picks, setPicks] = useState<Record<string, string[]>>({});
  const [notes, setNotes] = useState('');
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }

    document.addEventListener('keydown', onKey);

    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function toggleAddOn(addOn: AddOn) {
    setChosenAddOns((current) =>
      current.some((a) => a.id === addOn.id)
        ? current.filter((a) => a.id !== addOn.id)
        : [...current, addOn],
    );
  }

  const groups = product.options ?? [];

  function pickedIn(groupId: string) {
    return picks[groupId] ?? [];
  }

  /** Adds or removes a pick, respecting the group's maximum. */
  function togglePick(group: ProductOptionGroup, choiceId: string) {
    setPicks((current) => {
      const chosen = current[group.id] ?? [];

      if (group.maxSelections === 1) {
        // Single-choice groups behave like radio buttons.
        return { ...current, [group.id]: chosen[0] === choiceId ? [] : [choiceId] };
      }

      return chosen.length >= group.maxSelections
        ? current
        : { ...current, [group.id]: [...chosen, choiceId] };
    });
  }

  function removePick(groupId: string, index: number) {
    setPicks((current) => {
      const chosen = [...(current[groupId] ?? [])];
      chosen.splice(index, 1);
      return { ...current, [groupId]: chosen };
    });
  }

  const chosenOptions: ChosenOption[] = groups.flatMap((g) =>
    pickedIn(g.id).map((choiceId) => {
      const choice = g.choices.find((c) => c.id === choiceId)!;
      return {
        groupId: g.id,
        groupName: g.name,
        choiceId: choice.id,
        label: choice.label,
        extraPrice: choice.extraPrice,
      };
    }),
  );

  // The same rule the server enforces, shown before they press the button
  // rather than as a rejection afterwards.
  const missing = groups
    .filter((g) => pickedIn(g.id).length < g.minSelections)
    .map((g) =>
      g.minSelections === g.maxSelections
        ? `${g.name}: choose ${g.minSelections}`
        : `${g.name}: choose at least ${g.minSelections}`,
    );

  const unit =
    variant.price +
    chosenAddOns.reduce((sum, a) => sum + a.price, 0) +
    chosenOptions.reduce((sum, c) => sum + c.extraPrice, 0);

  function submit() {
    if (missing.length > 0) return;
    add(product, variant, chosenAddOns, notes.trim() || null, quantity, earnsStamp, chosenOptions);
    onClose();
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={product.name}>
      <div className="modal__backdrop" onClick={onClose} />
      <div className="modal__panel">
        <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <h2>{product.name}</h2>
        {product.description && <p className="modal__desc">{product.description}</p>}
        {earnsStamp && <p className="hint-good small">🥤 Earns a loyalty stamp</p>}

        {groups.map((group) => {
          const chosen = pickedIn(group.id);
          const full = chosen.length >= group.maxSelections;

          return (
            <fieldset className="field" key={group.id}>
              <legend>
                {group.name}
                {group.isRequired && <span className="req"> *</span>}
              </legend>

              {group.helpText && <p className="muted small">{group.helpText}</p>}

              {group.maxSelections > 1 && (
                <p className="muted small">
                  {chosen.length} of {group.maxSelections} chosen
                  {group.maxSelections > 1 && ' — you can repeat a flavour'}
                </p>
              )}

              <div className="chips">
                {group.choices.map((choice) => {
                  const selected = chosen.includes(choice.id);

                  return (
                    <button
                      key={choice.id}
                      type="button"
                      className={selected && group.maxSelections === 1 ? 'chip is-active' : 'chip'}
                      disabled={full && !selected && group.maxSelections === 1}
                      onClick={() => togglePick(group, choice.id)}
                    >
                      {choice.label}
                      {choice.extraPrice > 0 && ` +${money(choice.extraPrice)}`}
                    </button>
                  );
                })}
              </div>

              {/* For multi-pick groups the running list is what the customer
                  actually reads back, since a chip cannot show "x3". */}
              {group.maxSelections > 1 && chosen.length > 0 && (
                <ul className="picks">
                  {chosen.map((id, index) => (
                    <li key={`${id}-${index}`}>
                      <span>{group.choices.find((c) => c.id === id)?.label}</span>
                      <button type="button" onClick={() => removePick(group.id, index)} aria-label="Remove">
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </fieldset>
          );
        })}

        <fieldset className="field">
          <legend>Size</legend>
          <div className="chips">
            {product.variants.map((v) => (
              <button
                key={v.id}
                type="button"
                className={v.id === variant.id ? 'chip is-active' : 'chip'}
                onClick={() => setVariant(v)}
                aria-pressed={v.id === variant.id}
              >
                <span>{v.label}</span>
                <strong>{money(v.price)}</strong>
              </button>
            ))}
          </div>
        </fieldset>

        {addOns.length > 0 && (
          <fieldset className="field">
            <legend>Extras</legend>
            <div className="chips chips--wrap">
              {addOns.map((addOn) => (
                <button
                  key={addOn.id}
                  type="button"
                  className={
                    chosenAddOns.some((a) => a.id === addOn.id) ? 'chip chip--sm is-active' : 'chip chip--sm'
                  }
                  onClick={() => toggleAddOn(addOn)}
                  aria-pressed={chosenAddOns.some((a) => a.id === addOn.id)}
                >
                  <span>{addOn.name}</span>
                  <strong>{addOn.price === 0 ? 'FREE' : `+${money(addOn.price)}`}</strong>
                </button>
              ))}
            </div>
          </fieldset>
        )}

        <label className="field">
          <span>Note for the barista (optional)</span>
          <input
            type="text"
            value={notes}
            maxLength={200}
            placeholder="e.g. no honey, extra cold"
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        <div className="modal__foot">
          <div className="qty">
            <button type="button" onClick={() => setQuantity((q) => Math.max(1, q - 1))} aria-label="Fewer">
              −
            </button>
            <span aria-live="polite">{quantity}</span>
            <button type="button" onClick={() => setQuantity((q) => Math.min(50, q + 1))} aria-label="More">
              +
            </button>
          </div>

          <button
            type="button"
            className="btn btn--primary btn--block"
            disabled={missing.length > 0}
            onClick={submit}
          >
            Add to cart · {money(unit * quantity)}
          </button>
        </div>

        {/* Says what is still needed rather than leaving a dead button. */}
        {missing.length > 0 && <p className="modal__missing">{missing.join(' · ')}</p>}

        <p className="modal__hint">
          <Link to="/cart">View cart</Link>
        </p>
      </div>
    </div>
  );
}
